import { defaultSchema } from 'rehype-sanitize';
import type { Schema } from 'hast-util-sanitize';

/**
 * Curated sanitize schema — the SECURITY BOUNDARY (spec §7).
 *
 * Applied to user-controlled HTML BEFORE trusted generators (KaTeX, Shiki,
 * directive callouts) run, so those generators' classes/styles are never
 * stripped. `defaultSchema` is already conservative (no `<script>`, no event
 * handlers, safe href/src protocols, GFM tables). We extend it minimally:
 *
 *  - allow task-list checkbox inputs (GFM)
 *  - keep `className` on code/span/div for later highlighting + callouts
 *  - allow `dataLine`, `dataSourceStart`, `dataSourceEnd` (the `data-line`,
 *    `data-source-start`, `data-source-end` attributes) on ALL elements so
 *    the source-range metadata stamped by `rehypeSourceLines` survives
 *    sanitize. They are harmless numeric attributes used for outline jump,
 *    scroll sync, and the Copy-as-markdown affordance — allowing them does
 *    NOT weaken XSS protection (no URL/script semantics).
 *  - DO NOT add `data:` to protocols. All `data:` URLs are stripped here;
 *    Plan 3 adds a post-sanitize transform that allow-lists base64 raster
 *    images (png/jpeg/gif/webp) while still rejecting `data:image/svg+xml`.
 */
const base = defaultSchema as Schema;

export const sanitizeSchema: Schema = {
  ...base,
  tagNames: [...(base.tagNames ?? [])],
  attributes: {
    ...base.attributes,
    '*': [
      ...(base.attributes?.['*'] ?? []),
      'className',
      'dataLine',
      // Source-range offsets stamped by rehypeSourceLines so the Copy-as-
      // markdown affordance can slice the original markdown. Both are
      // plain numeric attributes — no URL/script semantics.
      'dataSourceStart',
      'dataSourceEnd',
    ],
    input: [...(base.attributes?.input ?? []), 'type', 'checked', 'disabled'],
    span: [...(base.attributes?.span ?? []), 'className'],
    div: [...(base.attributes?.div ?? []), 'className'],
    code: [...(base.attributes?.code ?? []), 'className'],
  },
  protocols: {
    ...base.protocols,
    href: ['http', 'https', 'mailto'],
    src: ['http', 'https'],
  },
};
